import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { refreshPostgresFacetCounts } from "../../../utils/refreshPostgresFacetCounts";

/** A worklist side-nav entry; `id` is the tab segment matched against the path. */
type SideLink = { id: string; label: string };

/**
 * Refreshes Postgres facet counts whenever the user lands on a worklist tab route.
 *
 * The Worklists layout stays mounted while navigating into nested pages (e.g. an
 * order detail), so depending on `providerInfo` alone misses re-entry when it is
 * already cached and unchanged. This hook refetches on the rising edge of entering
 * a tab route OR when the provider/facility changes, deduped via refs so sibling
 * tab switches and the initial load don't trigger redundant fetches.
 *
 * @param sideLinks - Worklist tab entries; their `id`s define which paths count as
 *   list routes (vs. nested detail pages) that should trigger a refresh.
 */
export const useRefreshFacetCountsOnRouteEntry = (sideLinks: SideLink[]) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { data: providerInfo } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  // True only when the current path is one of the worklist tab routes (a sideLink),
  // i.e. a list view that shows the facet-driven side nav — not a nested detail page.
  const isOnWorklistTabRoute = useMemo(() => {
    const pathAfterWorklists =
      location.pathname.replace(/^\/worklists\/?/, "") || "";
    return (
      sideLinks.length > 0 &&
      sideLinks.some((link) => link.id === pathAfterWorklists)
    );
  }, [location.pathname, sideLinks]);

  const previousProviderInfoRef = useRef<typeof providerInfo>(null);
  const wasOnWorklistTabRouteRef = useRef(false);

  useEffect(() => {
    if (!isOnWorklistTabRoute) {
      wasOnWorklistTabRouteRef.current = false;
      return;
    }
    if (!providerInfo) {
      return;
    }

    const providerChanged = previousProviderInfoRef.current !== providerInfo;
    const enteredRoute = !wasOnWorklistTabRouteRef.current;
    if (providerChanged || enteredRoute) {
      refreshPostgresFacetCounts(dispatch);
    }

    previousProviderInfoRef.current = providerInfo;
    wasOnWorklistTabRouteRef.current = true;
  }, [providerInfo, isOnWorklistTabRoute, dispatch]);
};
